import {
    useRef
} from "react";
import {
    TouchableOpacity,
    PanResponder,
    Animated,
    Image,
    View
} from "react-native";
import type NotificationCardProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Text
} from "ncore-ui-kit";
import {
    Megaphone,
    Newspaper,
    Calendar,
    FileText,
    Trash2,
    Bell
} from "lucide-react-native";
import moment from "moment";

const ACTION_WIDTH = 80;

const NotificationCard = ({
    onDelete,
    onPress,
    item
}: NotificationCardProps) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const translateX = useRef(new Animated.Value(0)).current;

    const isOpenRef = useRef(false);

    const panResponder = useRef(
        PanResponder.create({
            onMoveShouldSetPanResponder: (_, gestureState) => {
                return Math.abs(gestureState.dx) > 10 && Math.abs(gestureState.dx) > Math.abs(gestureState.dy);
            },
            onPanResponderMove: (_, gestureState) => {
                if(isOpenRef.current) {
                    const nextValue = gestureState.dx - ACTION_WIDTH;
                    if(nextValue <= 0 && nextValue >= -ACTION_WIDTH) {
                        translateX.setValue(nextValue);
                    }
                } else if(gestureState.dx <= 0 && gestureState.dx >= -ACTION_WIDTH) {
                    translateX.setValue(gestureState.dx);
                }
            },
            onPanResponderRelease: (_, gestureState) => {
                if(gestureState.dx < -ACTION_WIDTH / 2 || gestureState.vx < -0.5) {
                    Animated.spring(translateX, {
                        useNativeDriver: true,
                        toValue: -ACTION_WIDTH,
                        bounciness: 4
                    }).start();
                    isOpenRef.current = true;
                } else {
                    Animated.spring(translateX, {
                        useNativeDriver: true,
                        bounciness: 4,
                        toValue: 0
                    }).start();
                    isOpenRef.current = false;
                }
            }
        })
    ).current;

    const handleDeletePress = () => {
        Animated.timing(translateX, {
            useNativeDriver: true,
            toValue: -400,
            duration: 200
        }).start(() => {
            onDelete?.(item._id);
        });
    };

    const handleCardPress = () => {
        if(isOpenRef.current) {
            Animated.spring(translateX, {
                useNativeDriver: true,
                bounciness: 4,
                toValue: 0
            }).start();
            isOpenRef.current = false;
            return;
        }
        onPress();
    };

    const renderIcon = () => {
        if(item.imageUrl) {
            return <Image
                source={{
                    uri: item.imageUrl
                }}
                style={[
                    stylesheet.thumbnailImage,
                    {
                        borderRadius: radiuses.md
                    }
                ]}
            />;
        }

        const iconColor = item.isRead
            ? colors.content.icon.default
            : colors.system.default.info;

        const renderTypeIcon = () => {
            if(item.type === "ANNOUNCEMENT") {
                return <Megaphone
                    color={iconColor}
                    size={22}
                />;
            }
            if(item.type === "NEWS") {
                return <Newspaper
                    color={iconColor}
                    size={22}
                />;
            }
            if(item.type === "EVENT") {
                return <Calendar
                    color={iconColor}
                    size={22}
                />;
            }
            if(item.type === "FORM") {
                return <FileText
                    color={iconColor}
                    size={22}
                />;
            }
            return <Bell
                color={iconColor}
                size={22}
            />;
        };

        return <View
            style={[
                stylesheet.iconContainer,
                {
                    backgroundColor: item.isRead
                        ? colors.content.container.subtle
                        : colors.content.container.info,
                    borderRadius: radiuses.md
                }
            ]}
        >
            {renderTypeIcon()}
        </View>;
    };

    return <View
        style={[
            stylesheet.wrapper,
            {
                marginBottom: spaces.spacingSm,
                borderRadius: radiuses.md
            }
        ]}
    >
        <View
            style={[
                stylesheet.deleteActionContainer,
                {
                    borderBottomRightRadius: radiuses.md,
                    borderTopRightRadius: radiuses.md,
                    backgroundColor: colors.system.default.error,
                    width: ACTION_WIDTH
                }
            ]}
        >
            <TouchableOpacity
                onPress={handleDeletePress}
                activeOpacity={0.8}
                style={stylesheet.deleteActionButton}
            >
                <Trash2
                    color={colors.project.brandWhite ?? "#FFFFFF"}
                    size={22}
                />
                <Text
                    variant="labelSmallSize"
                    style={[
                        stylesheet.deleteActionText,
                        {
                            color: colors.project.brandWhite ?? "#FFFFFF",
                            marginTop: spaces.spacingXs
                        }
                    ]}
                >
                    {localize("delete")}
                </Text>
            </TouchableOpacity>
        </View>
        <Animated.View
            {...panResponder.panHandlers}
            style={[
                {
                    transform: [
                        {
                            translateX
                        }
                    ]
                }
            ]}
        >
            <TouchableOpacity
                onPress={handleCardPress}
                activeOpacity={0.85}
                style={[
                    stylesheet.cardContainer,
                    {
                        backgroundColor: colors.content.container.mid,
                        borderLeftColor: item.isRead
                            ? colors.content.border.subtle
                            : colors.system.default.info,
                        borderLeftWidth: item.isRead
                            ? borders.line
                            : 4,
                        borderRadius: radiuses.md,
                        borderColor: item.isRead
                            ? colors.content.border.subtle
                            : colors.content.border.emphasized,
                        borderWidth: borders.line,
                        padding: spaces.spacingMd
                    }
                ]}
            >
                {renderIcon()}
                <View
                    style={[
                        stylesheet.contentContainer,
                        {
                            marginLeft: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.headerRow}
                    >
                        <Text
                            variant="bodyLargeSize"
                            numberOfLines={1}
                            color="high"
                            style={stylesheet.titleText}
                        >
                            {item.title}
                        </Text>
                        {!item.isRead && <View
                            style={[
                                stylesheet.unreadIndicatorDot,
                                {
                                    backgroundColor: colors.system.default.info,
                                    borderRadius: radiuses.full
                                }
                            ]}
                        />}
                    </View>
                    <Text
                        variant="bodyMediumSize"
                        numberOfLines={2}
                        color="mid"
                        style={[
                            stylesheet.descriptionText,
                            {
                                marginTop: spaces.spacingXs
                            }
                        ]}
                    >
                        {item.description}
                    </Text>
                    <View
                        style={[
                            stylesheet.footerRow,
                            {
                                marginTop: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="labelSmallSize"
                            color="low"
                            style={stylesheet.dateText}
                        >
                            {moment(item.createdAt).format("DD.MM.YYYY, HH:mm")}
                        </Text>
                    </View>
                </View>
            </TouchableOpacity>
        </Animated.View>
    </View>;
};
export default NotificationCard;
